import { useCallback, useMemo, useState } from 'react';

import { ApiFill } from '#/api/types';

import { aggregateFillsByOrder } from './aggregateFills';

type FillRow = ApiFill & {
  /** Set when fills are aggregated by order, see aggregateFillsByOrder */
  aggregatedFillsCount?: number;
  aggregationKey?: string;
  aggregationExpanded?: boolean;
  aggregatedFillIndex?: number;
};

export type TradesTableRow = FillRow & {
  baseAssetSymbol: string;
};

/**
 * Prepares fills for MyTradesTable - optionally merged by order execution,
 * with a single aggregation group expandable into its individual fills.
 */
export function useTradesTableRows(
  fills: ApiFill[] | undefined,
  aggregateByOrder: boolean
) {
  // Single aggregation group can be expanded to display its individual fills
  const [expandedAggregationKey, setExpandedAggregationKey] =
    useState<string>();

  const trades = useMemo((): TradesTableRow[] => {
    const list = fills || [];
    const rows: FillRow[] = aggregateByOrder
      ? aggregateFillsByOrder(list, expandedAggregationKey)
      : list;
    return rows.map(fill => ({
      ...fill,
      baseAssetSymbol: fill.market.split('-')[0],
    }));
  }, [fills, aggregateByOrder, expandedAggregationKey]);

  const onToggleAggregation = useCallback(
    (aggregationKey: string) =>
      setExpandedAggregationKey(current =>
        current === aggregationKey ? undefined : aggregationKey
      ),
    []
  );

  return {
    trades,
    onToggleAggregation: aggregateByOrder ? onToggleAggregation : undefined,
  };
}
